import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { actors } from "@/mocks/actors";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

const PAGE_STEP = 6;

export default function Actors() {
  const [keyword, setKeyword] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);

  useSeo({
    title: "演员资料 - 蜜桃成熟时电影",
    description:
      "蜜桃成熟时系列演员资料库，收录主要演员的简介、代表作品与参演影片列表，支持按姓名搜索快速查找。",
    keywords: "蜜桃成熟时演员,电影演员,演员资料,演员列表,主演",
  });

  const filtered = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    if (!query) return actors;
    return actors.filter(
      (actor) =>
        actor.name.toLowerCase().includes(query) || actor.englishName.toLowerCase().includes(query),
    );
  }, [keyword]);

  const visibleActors = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-7 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "演员资料" }]} />
          <div className="mt-4 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                演员资料
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                收录系列中重要的演员档案，包含个人简介与参演作品。点击卡片可以查看完整资料与对应影片条目。
              </p>
            </div>
            <div className="w-full md:w-[280px]">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 py-2 focus-within:border-primary-400">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-user-search-line text-sm text-foreground-400" />
                </span>
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => {
                    setKeyword(event.target.value);
                    setVisibleCount(PAGE_STEP);
                  }}
                  placeholder="输入演员姓名搜索"
                  aria-label="搜索演员姓名"
                  className="w-full min-w-0 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                {keyword && (
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    aria-label="清空搜索"
                    className="flex h-4 w-4 items-center justify-center text-foreground-400 hover:text-accent-600"
                  >
                    <i className="ri-close-circle-fill text-sm" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-foreground-500">
            {keyword ? (
              <>
                「<span className="text-foreground-800">{keyword.trim()}</span>」的搜索结果：
                <span className="font-medium text-accent-600">{filtered.length}</span> 位演员
              </>
            ) : (
              <>
                共 <span className="font-medium text-accent-600">{actors.length}</span> 位演员资料
              </>
            )}
          </p>
          <Link to="/category" className="text-xs text-foreground-500 transition-colors hover:text-accent-600">
            按影片浏览
            <i className="ri-arrow-right-line ml-1" />
          </Link>
        </div>

        {visibleActors.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {visibleActors.map((actor) => {
                const filmTitles = actor.films
                  .map((filmId) => movies.find((movie) => movie.id === filmId))
                  .filter((movie): movie is (typeof movies)[number] => Boolean(movie))
                  .slice(0, 2);
                return (
                  <Link
                    key={actor.id}
                    to={`/actors/${actor.id}`}
                    className="group overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-colors hover:border-primary-300"
                  >
                    <div className="relative aspect-square w-full overflow-hidden bg-background-200">
                      <img
                        src={actor.avatar}
                        alt={`${actor.name}头像`}
                        title={`${actor.name}演员资料与参演作品`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-foreground-950/70 to-transparent" />
                      <span className="absolute bottom-2.5 left-3 text-[10px] uppercase tracking-[0.16em] text-background-100/85">
                        {actor.englishName}
                      </span>
                    </div>
                    <div className="p-3.5 md:p-4">
                      <h2 className="font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-accent-600">
                        {actor.name}
                      </h2>
                      <p className="mt-1 text-[11px] text-foreground-400">
                        {actor.birthYear} 年 · {actor.birthplace}
                      </p>
                      <p className="mt-2 line-clamp-1 text-xs text-foreground-500">
                        代表作：{filmTitles.map((movie) => movie.title).join("、")}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col items-center gap-2">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + PAGE_STEP)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-accent-600"
                >
                  <i className="ri-add-line" />
                  加载更多（还有 {filtered.length - visibleCount} 位）
                </button>
              ) : (
                <p className="text-[11px] text-foreground-400">已展示全部 {filtered.length} 位演员</p>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-user-search-line text-xl" />
            </span>
            <h2 className="font-heading text-base font-semibold text-foreground-900">没有找到这位演员</h2>
            <p className="max-w-sm text-xs leading-relaxed text-foreground-500">
              试试输入中文姓名或英文名，也可以浏览全部演员资料。
            </p>
            <button
              type="button"
              onClick={() => setKeyword("")}
              className="mt-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部演员
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}